//================================================================
//  Component: Requests Pane
//================================================================
//
//  Purpose: Links 'application' or 'document' documents to a 'requestid'
//
//  Properties:
//    - collectionid = string of the collection id, used as part of role checks
//    - document = {useState, this contains the application or agreement document}
//    - setDocument = {useState, used to determine if the modal is open/closed and contains the application/agreement doc}
//
//  RequestsPane:
//    <RequestsPane
//      document={taskPane}
//      setDocument={setTaskPane}
//    ></RequestsPane>    

//================================================================

//Libraries
import React, { useState, useContext, useEffect, useReducer } from 'react';

//Contexts
import { GetUser, GetRoles, SetToast } from '../../Library/GlobalContexts';

//Components
import StatusLabel from '../StatusLabel/StatusLabel';
import ExportToCSV from '../ExportToCSV/ExportToCSV';
import Tooltip from '../Tooltip/Tooltip';

//Functions
import GetCollection from '../../Library/GetCollection';
import WriteDocument from '../../Library/WriteDocument';
import CheckInventoryRole from '../../Library/CheckInventoryRole';
import ConvertDate from '../../Library/ConvertDate';

//Images
import IconSearch from '../Images/Icon_Search_Grey.svg';
import ClearSearch from '../Images/Icon_Clear_Grey.svg';
import Add from '../Images/Icon_Add_Teal.svg';
import AddDisabled from '../Images/Icon_AddDisabled_Teal.svg';
import LoadingIcon from '../Images/Image_Loading_Ripple.svg';


export default function RequestsPane({
    collectionid,
    document,
    setDocument,
}) {

    //------------------------------------------------------
    //  useContext
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const getRoles = useContext(GetRoles);
    const setToast = useContext(SetToast);

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // Used to determine the status of the pane > 'pending', 'onload'
    const [paneStatus, setPaneStatus] = useState('pending');

    // Role checks
    const [roleStatus, setRoleStatus] = useState('read'); // read, download, create, write & admin

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store details about the requests
    const [requestData, setRequestData] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'allRequests': [],
            'linkedRequests': [],
            'filteredRequests': [],
            'addedRequests': [],
            'removedRequests': [],

            // Add Requests Search
            'addRequestsSearchInput': '',
            'addRequestSearchResults': [],

            // Table Search
            'tableSearchInput': ''
        }
    );

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Search for existing requests to add to application/agreement
    const handleAllRequestsSearch = (value) => {

        const searchResults = requestData?.allRequests?.filter((object) =>
            object?.requestid?.toLowerCase().includes(value.toLowerCase())

        );

        // Turn Dropdown on if field is not empty
        setRequestData({
            'addRequestSearchResults': searchResults
        });

    };

    // Used to search the table
    const handleTableSearch = (value) => {

        setRequestData({
            'tableSearchInput': value
        });

        // Reset the Filter
        if (value.length === 0) {
            setRequestData({
                'filteredRequests': requestData?.linkedRequests
            });
        };

        // Search Filter
        //  1. requestid
        //  2. createdby.givenname
        //  3. createdby.surname
        //  4. createdby.email
        //  5. status
        const searchResults = requestData?.linkedRequests?.filter((object) =>
            object?.requestid?.toLowerCase().includes(value.toLowerCase()) ||
            object?.tasktype?.toLowerCase().includes(value.toLowerCase()) ||
            object?.createdby?.email?.toLowerCase().includes(value.toLowerCase()) ||
            object?.status?.toLowerCase().includes(value.toLowerCase())
        );

        setRequestData({
            'filteredRequests': searchResults
        });

    }

    // Submits form changes
    const handleSubmit = () => {

        const docWritePromises = [];

        // Update last modifiedby
        const lastmodifiedby = {
            'email': getUser?.emailaddress,
            'givenname': getUser?.givenname,
            'surname': getUser?.surname
        };

        // If Linking Request to Agreement
        if (document.agreementid !== undefined) {

            // Add Requests
            requestData?.addedRequests?.forEach((object) => {

                document.linkedrequests[object.requestid] = object;
                document.lastmodifiedby = lastmodifiedby;

                docWritePromises.push(
                    WriteDocument('agreements', document.agreementid, document, true),
                );

            });

            // Remove Requests
            requestData?.removedRequests?.forEach((object) => {

                delete document.linkedrequests[object.requestid];
                document.lastmodifiedby = lastmodifiedby;

                docWritePromises.push(
                    WriteDocument('agreements', document.agreementid, document, false),
                );

            });

        }

        // If Linking Request to Application
        if (document.applicationid !== undefined) {

            // Add Requests
            requestData?.addedRequests?.forEach((object) => {

                document.linkedrequests[object.requestid] = object;
                document.lastmodifiedby = lastmodifiedby;

                docWritePromises.push(
                    WriteDocument('applications', document.applicationid, document, true),
                );

            });

            // Remove Requests
            requestData?.removedRequests?.forEach((object) => {

                delete document.linkedrequests[object.requestid];
                document.lastmodifiedby = lastmodifiedby;

                docWritePromises.push(
                    WriteDocument('applications', document.applicationid, document, false),
                );

            });

        }

        // Settle promises
        Promise.all(docWritePromises)
            .then(() => {

                // Success toast
                setToast({
                    'type': 'success',
                    'message': 'Successfully linked requests.'
                });

                // Reset and Close Side Pane
                setPaneStatus('pending');

                setDocument(undefined);

                setRequestData({
                    'allRequests': [],
                    'linkedRequests': [],
                    'filteredRequests': [],
                    'addedRequests': [],
                    'removedRequests': [],

                    // Add Requests Search
                    'addRequestsSearchInput': '',
                    'addRequestSearchResults': [],

                    // Table Search
                    'tableSearchInput': ''
                });

            })
            .catch((error) => {

                console.log('error', error);

                // Set error on toast
                setToast({
                    'type': 'error',
                    'message': 'Failed to save changes',
                });

            });

    }

    // Create data format for CSV Export
    function getDataObject(requests) {

        const data = [];

        requests.forEach((request) => {

            // Generic requests schema
            data.push({
                'Customer Request ID': request?.parentrequestid,
                'Requester': request?.createdby?.email,
                'Request ID': request?.requestid === undefined ? '-' : request?.requestid,
                'PoA Number': request?.poanumber === undefined ? '-' : request?.poanumber,
                'Task Name': request?.taskname,
                'Short Description': request?.shortdescription,
                'Application Name': request?.applicationname,
                'Last Modified By': request?.lastmodifiedby?.email,
                'Last Modified Date': ConvertDate(request?.lastmodifieddate),
            });

        });

        return data;
    }

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Onload > Get all requests 
    useEffect(() => {

        // Close the pane if there are issues
        if (document === undefined) return;
        if (document?.linkedrequests === undefined) return;
        if (paneStatus === 'onload') return;
        if (getRoles === undefined) return;

        // ============================================
        //  Perform role check
        // ============================================

        const roleStatus = CheckInventoryRole(collectionid, getRoles, getUser);
        if (roleStatus === 'access-denied') return setDocument(undefined); // Close pane
        setRoleStatus(roleStatus);

        // ============================================
        //  Fetch Data
        // ============================================

        GetCollection('requests').then((allRequests) => {

            const linkedRequests = Object.values(document?.linkedrequests);

            setRequestData({
                'allRequests': allRequests,
                'linkedRequests': linkedRequests,
                'filteredRequests': linkedRequests,
                'addedRequests': [],
                'removedRequests': [],
            });

            setPaneStatus('onload');

        }).catch((error) => {

            console.log('error', error);

            // Set error on toast
            setToast({
                'type': 'error',
                'message': `Failed to get task - ${error}`,
            });

        });

        // eslint-disable-next-line
    }, [document, getRoles, getUser]);

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    if (document === undefined) return null;

    //====================================================
    //  Pending
    //====================================================

    if (paneStatus === 'pending') {
        return (
            <div className='Pane-Background'>
                <dialog className='Application-Details-Pane-Container flex flex-col gap-2 pb-0 items-center'>

                    <img className='w-[200px] self-center' alt='loading-circle-icon' src={LoadingIcon}></img>

                    <button className='Primary-Button w-fit self-center' onClick={() => {

                        setDocument(undefined);
                        setPaneStatus('pending');

                    }}>
                        Cancel
                    </button>

                </dialog >
            </div>
        )
    }

    //====================================================
    //  Onload
    //====================================================

    else if (paneStatus === 'onload') {
        return (
            <div className='Pane-Background'>
                <dialog className='Application-Details-Pane-Container'>

                    <div className='grid grid-rows-[170px_1fr_70px] items-center justify-between w-full overflow-hidden' >

                        <div className='flex flex-col gap-2'>

                            {/* ------------------------------------------------------ */}
                            {/*  Header Container                                      */}
                            {/* ------------------------------------------------------ */}

                            <div className='flex flex-col gap-1 px-4 py-2 border-solid border-b border-b-[#D8D8D8]'>

                                {/* Title */}
                                <h5>Linked Requests | {document.poanumber !== undefined ? document.poanumber : document.applicationname !== undefined ? document.applicationname : ''}</h5>
                                <span className='text-[#424242] text-sm' hidden={document?.vendor?.vendorname?.length === 0}>
                                    {document.vendor.vendorname}
                                </span>

                            </div>

                            {/* ------------------------------------------------------ */}
                            {/*  Add Request Search Bar                                */}
                            {/* ------------------------------------------------------ */}

                            <div className='relative my-[10px] px-4'>

                                {/* Search Bar */}
                                <div className='flex flex-row items-center'>

                                    {/* Search Input Field */}
                                    <input
                                        className={
                                            requestData?.addRequestSearchResults?.length > 0 ?
                                                'w-full h-[45px] px-[15px] py-[10px] bg-[white] border border-solid border-[#7c7c7c] rounded-tl-[5px] focus:outline-none'
                                                :
                                                `w-full h-[45px] px-[15px] py-[10px] bg-[white] border border-solid border-[#7c7c7c] rounded-[5px] focus:outline-none ${(roleStatus === 'read' || roleStatus === 'download' || roleStatus === 'create') && 'cursor-not-allowed bg-[#f8f8f8]'}`
                                        }
                                        disabled={(roleStatus === 'read' || roleStatus === 'download' || roleStatus === 'create')}
                                        type='text'
                                        placeholder='Search existing requests to add to agreement'
                                        autoComplete='off'
                                        onChange={(evt) => {

                                            const value = evt.target.value;
                                            setRequestData({ 'addRequestsSearchInput': value });

                                            // Hide search
                                            if (value.length === 0) {

                                                return setRequestData({
                                                    'addRequestsSearchInput': '',
                                                    'addRequestSearchResults': []
                                                })

                                            }

                                            // Trigger search
                                            handleAllRequestsSearch(value);

                                        }}
                                        value={requestData?.addRequestsSearchInput}
                                    ></input>

                                    {/* Clear Search Button */}
                                    {
                                        requestData?.addRequestSearchResults?.length > 0 &&
                                        <div className='animate-fadein  bg-[#F1F1F1] h-[45px] p-[15px] shadow-md border border-solid border-[#080808] border-l-[none] rounded-tl-none rounded-br-none rounded-tr-[5px] rounded-bl-none text-center align-center cursor-pointer'>
                                            <img className='self-center' src={ClearSearch} alt='ClearSearch' onClick={() => setRequestData({
                                                'addRequestsSearchInput': '',
                                                'addRequestSearchResults': []
                                            })}></img>
                                        </div>
                                    }

                                </div>

                                {/* Search Results */}
                                {
                                    requestData?.addRequestSearchResults?.length > 0 &&
                                    <div className='absolute z-10 max-h-[350px] w-[calc(100%-48px)] overflow-y-auto bg-white shadow-lg border-[1px] border-[solid] border-[#D2D2D2]'>
                                        {
                                            requestData?.addRequestSearchResults?.map((request, index) => (
                                                <div key={index} className='border-b border-b-[#E1E1E1] last:border-0 hover:bg-[#F0F0F0]'>

                                                    {/* Request Detail */}
                                                    {
                                                        // Check if the request has already been linked
                                                        requestData?.linkedRequests?.filter(object => object.requestid === request.requestid)?.length > 0 ?

                                                            // Disabled State
                                                            (
                                                                <Tooltip
                                                                    message={`This request has already been linked to this ${document?.agreementid !== undefined ? 'agreement' : 'application'} `}
                                                                    children={
                                                                        <div className='w-full flex flex-row justify-between py-[10px] px-[15px]'>
                                                                            <div className='flex flex-col'>
                                                                                <p className='m-0 font-medium text-[14px] text-[#C4C4C4] uppercase'>
                                                                                    {request.requestid}
                                                                                </p>
                                                                                <p className='m-0 text-[14px] text-[#C4C4C4]'>
                                                                                    {request?.taskname}
                                                                                </p>
                                                                            </div>

                                                                            <img className='w-[28px]' src={AddDisabled} alt='Add'></img>
                                                                        </div>
                                                                    }
                                                                ></Tooltip>

                                                            )
                                                            :

                                                            // Active State
                                                            (
                                                                <div className='flex flex-row justify-between py-[10px] px-[15px] cursor-pointer'
                                                                    onClick={() => {

                                                                        // Define a new 'linkedRequests' array
                                                                        const newLinkedRequests = [
                                                                            ...requestData?.linkedRequests,
                                                                            {
                                                                                'requestid': request.requestid,
                                                                                'tasktype': request.tasktype,
                                                                                'stage': request.tasktype,
                                                                                'parentrequestid': request.parentrequestid,
                                                                                'assignedto': request.assignedto,
                                                                                'createddate': request.createddate,
                                                                                'createdby': request.createdby,
                                                                                'lastmodifieddate': request.lastmodifieddate,
                                                                                'lastmodifiedby': request.lastmodifiedby,
                                                                                'status': request.status,
                                                                            }
                                                                        ];

                                                                        setRequestData({
                                                                            'linkedRequests': newLinkedRequests,
                                                                            'filteredRequests': newLinkedRequests,
                                                                            'addRequestsSearchInput': '',
                                                                            'addRequestSearchResults': [],
                                                                            'addedRequests': newLinkedRequests
                                                                        });

                                                                    }}
                                                                >
                                                                    <div className='flex flex-col'>
                                                                        <p className='m-0 font-medium text-[14px] text-[#424242] disabled:text-[#C4C4C4] uppercase'>
                                                                            {request.requestid}
                                                                        </p>
                                                                        <p className='m-0 text-[14px] text-[#A0A0A0] disabled:text-[#C4C4C4]'>
                                                                            {request?.taskname}
                                                                        </p>
                                                                    </div>

                                                                    {/* Add Button */}
                                                                    <img className='w-[28px]' src={Add} alt='Add'></img>

                                                                </div>
                                                            )
                                                    }

                                                </div>
                                            ))
                                        }

                                        {/* Total Search Results */}
                                        <div className='text-sm py-[10px] px-[15px]'>
                                            Found {requestData?.addRequestSearchResults?.length} result(s).
                                        </div>

                                    </div>
                                }

                            </div>
                        </div>

                        {/* ------------------------------------------------------ */}
                        {/*  Body Container                                        */}
                        {/* ------------------------------------------------------ */}

                        <div className='w-[88vw] h-full flex flex-col overflow-y-auto p-4 px-4 py-2 border-solid border shadow-inner'>

                            {/* ------------------------------------------------------ */}
                            {/*  Table Container                                       */}
                            {/* ------------------------------------------------------ */}

                            <div className='Table-Container overflow-hidden'>

                                {/* ======== Sorting and Filters ========== */}
                                <div className='flex flex-row justify-between py-[10px] px-[20px] rounded-tl-[10px] rounded-tr-[10px] border-b-[#D8D8D8] border-solid border-b bg-[#F5F5F5]'>

                                    <label className='font-semibold self-center'>
                                        Total: {requestData?.filteredRequests?.length} of {requestData?.linkedRequests?.length}
                                    </label>

                                    <div className='flex flex-row gap-2'>

                                        {/* ======== Search Bar ======== */}
                                        <div className='grid grid-cols-[30px_1fr] items-center gap-[15px] bg-white px-[5px] rounded-[5px] border-1 border-solid border-[#dee2e6] w-[400px]'>
                                            <img className='ml-[10px]' src={IconSearch} alt='search-icon'></img>
                                            <input
                                                className='border-none h-[36px] w-full p-0 m-0 outline-none bg-white'
                                                type='text'
                                                placeholder='Search linked requests'
                                                autoComplete='off'
                                                onChange={(e) => handleTableSearch(e.target.value)}
                                                value={requestData?.tableSearchInput}
                                            ></input>
                                        </div>

                                        {/* Download Report */}
                                        <ExportToCSV
                                            filename={`${document.poanumber !== undefined ? document.poanumber : document.applicationname !== undefined ? document.applicationname : ''}-${Date.now()}`}
                                            data={getDataObject(requestData?.filteredRequests)}
                                            disabled={!(roleStatus === 'download' || roleStatus === 'create' || roleStatus === 'write' || roleStatus === 'delete' || roleStatus === 'admin')}
                                        ></ExportToCSV>
                                    </div>

                                </div>

                                {/* ============= Table ============== */}
                                <div className='overflow-y-auto h-[calc(100vh-360px)]'>
                                    <table className='min-w-full max-h-96 table-auto'>
                                        <thead>
                                            <tr className='sticky top-0 bg-white border-b-[#D8D8D8] border-solid border-b-2'>
                                                <th className='py-[15px] px-[18px] font-semibold text-[#212529]'>Request ID</th>
                                                <th className='py-[15px] px-[18px] font-semibold text-[#212529]'>Task Type</th>
                                                <th className='py-[15px] px-[18px] font-semibold text-[#212529]'>Assigned To</th>
                                                <th className='py-[15px] px-[18px] font-semibold text-[#212529]'>Last Modified Date</th>
                                                <th className='py-[15px] px-[18px] font-semibold text-[#212529]'>Last Modified By</th>
                                                <th className='py-[15px] px-[18px] font-semibold text-[#212529]'>Status</th>
                                                <th className='py-[15px] px-[18px] font-semibold text-[#212529]'></th>
                                            </tr>
                                        </thead>
                                        <tbody >
                                            {
                                                // No requests found
                                                requestData?.filteredRequests.length === 0 ?
                                                    (
                                                        <tr>
                                                            <td colSpan={5}>
                                                                <div className='text-center p-[2%]'>
                                                                    No requests found.
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ) :
                                                    // Map in each task
                                                    requestData?.filteredRequests?.map((doc, index) => (
                                                        <tr key={index} className='border-b-[#C8C8C9] bg-[#FFFFFF] border-solid border-b last:border-0'>

                                                            {/* ======================================= */}
                                                            {/*             Request ID                  */}
                                                            {/* ======================================= */}

                                                            <td className='py-[15px] px-[18px] max-w-[160px] overflow-hidden uppercase'>
                                                                {
                                                                    // === Digital Request === //
                                                                    doc?.tasktype === 'Digital Request' ?

                                                                        <a
                                                                            href={
                                                                                process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285' ?
                                                                                    `https://devdemandmanagement.digitalservices.lendlease.com/requests/${doc?.requestid}` // dev
                                                                                    :
                                                                                    process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396' ?
                                                                                        `https://uatdemandmanagement.digitalservices.lendlease.com/requests/${doc?.requestid}` // uat
                                                                                        :
                                                                                        `https://demandmanagement.digitalservices.lendlease.com/requests/${doc?.requestid}` // prod
                                                                            }
                                                                            target='_blank'
                                                                            rel='noopener noreferrer'>
                                                                            {doc?.requestid}
                                                                        </a>

                                                                        // === Commercial Review or Digital Commercial Request === //
                                                                        : doc?.tasktype === 'Commercial Review' || doc?.tasktype === 'Digital Commercial Request' ?

                                                                            <a
                                                                                href={
                                                                                    process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285' ?
                                                                                        `https://devcommercial.digitalservices.lendlease.com/requests/${doc?.requestid}?view=triage` // dev
                                                                                        :
                                                                                        process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396' ?
                                                                                            `https://uatcommercial.digitalservices.lendlease.com/requests/${doc?.requestid}?view=triage` // uat
                                                                                            :
                                                                                            `https://commercial.digitalservices.lendlease.com/requests/${doc?.requestid}?view=triage` // prod
                                                                                }
                                                                                target='_blank'
                                                                                rel='noopener noreferrer'>
                                                                                {doc?.requestid}
                                                                            </a>

                                                                            // === Procurement === //
                                                                            : doc?.tasktype === 'Procurement' ?

                                                                                <a
                                                                                    href={
                                                                                        process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285' ?
                                                                                            `https://devprocurement.digitalservices.lendlease.com/requests/${doc?.requestid}` // dev
                                                                                            :
                                                                                            process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396' ?
                                                                                                `https://uatprocurement.digitalservices.lendlease.com/requests/${doc?.requestid}` // uat
                                                                                                :
                                                                                                `https://procurement.digitalservices.lendlease.com/requests/${doc?.requestid}` // prod
                                                                                    }
                                                                                    target='_blank'
                                                                                    rel='noopener noreferrer'>
                                                                                    {doc?.requestid}
                                                                                </a>

                                                                                // === Quote Review === //
                                                                                : doc?.tasktype === 'Quote Review' ?

                                                                                    <>{doc?.requestid}</>

                                                                                    // === Approval Hub === //
                                                                                    :
                                                                                    <a
                                                                                        href={
                                                                                            process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285' ?
                                                                                                `https://dev.digitalservices.lendlease.com/approvals/${doc?.requestid}` // dev
                                                                                                :
                                                                                                process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396' ?
                                                                                                    `https://uat.digitalservices.lendlease.com/approvals/${doc?.requestid}` // uat
                                                                                                    :
                                                                                                    `https://digitalservices.lendlease.com/approvals/${doc?.requestid}` // prod
                                                                                        }
                                                                                        target='_blank'
                                                                                        rel='noopener noreferrer'>
                                                                                        {doc?.requestid}
                                                                                    </a>
                                                                }


                                                            </td>

                                                            {/* Task Type */}
                                                            <td className='py-[15px] px-[18px] max-w-[160px] overflow-hidden'>
                                                                {doc?.tasktype}
                                                            </td>

                                                            {/* Assigned To */}
                                                            <td className='py-[15px] px-[18px] max-w-[160px] overflow-hidden'>
                                                                {

                                                                    doc?.assignedto?.email === 'Unassigned' ?
                                                                        <>
                                                                            <b className='font-medium text-[#424242]'>Unassigned</b>
                                                                            <br></br>
                                                                            <label className='text-sm'>---</label>
                                                                        </>
                                                                        :
                                                                        <>
                                                                            <b className='font-medium text-[#424242]'>{doc?.assignedto?.givenname} {doc?.assignedto?.surname}</b>
                                                                            <br></br>
                                                                            <label className='text-sm'>{doc?.assignedto?.email}</label>
                                                                        </>
                                                                }

                                                            </td>

                                                            {/* Last Modified Date */}
                                                            {/* Task Type */}
                                                            <td className='py-[15px] px-[18px] max-w-[160px] overflow-hidden'>
                                                                {ConvertDate(doc?.lastmodifieddate)}
                                                            </td>

                                                            {/* Last Modified By */}
                                                            <td className='py-[15px] px-[18px] max-w-[160px] overflow-hidden'>
                                                                <b className='font-medium text-[#424242]'>{doc?.lastmodifiedby?.givenname} {doc?.lastmodifiedby?.surname}</b>
                                                                <br></br>
                                                                <label className='text-sm'>{doc?.lastmodifiedby?.email}</label>
                                                            </td>

                                                            {/* Status Label */}
                                                            <td className='py-[15px] px-[18px] max-w-[160px] overflow-hidden'>
                                                                <StatusLabel status={doc?.status} tasktype={doc?.tasktype}></StatusLabel>
                                                            </td>

                                                            {/* Remove Icon */}
                                                            <td hidden={!(roleStatus === 'write' || roleStatus === 'delete' || roleStatus === 'admin')} className='py-[15px] px-[18px] max-w-[160px] overflow-hidden'>
                                                                <div className={`Cancel-Icon cursor-pointer min-w-[32px]`} onClick={() => {

                                                                    setRequestData(
                                                                        {
                                                                            'linkedRequests': requestData?.linkedRequests?.filter((object) => object !== doc),
                                                                            'filteredRequests': requestData?.linkedRequests?.filter((object) => object !== doc),
                                                                            'removedRequests': [...requestData?.removedRequests, doc]
                                                                        }
                                                                    )

                                                                }}></div>
                                                            </td>

                                                        </tr>
                                                    ))
                                            }
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                        </div>

                        {/* ------------------------------------------------------ */}
                        {/*  Buttons Container                                     */}
                        {/* ------------------------------------------------------ */}

                        <div className='flex flex-row gap-2 px-4 py-3'>

                            {/* Submit */}
                            <button disabled={!(roleStatus === 'write' || roleStatus === 'delete' || roleStatus === 'admin')} className='Primary-Button' type='submit' value='submit' onClick={() => handleSubmit()}>
                                Save
                            </button>

                            {/* Cancel */}
                            <button className='Secondary-Button' onClick={() => {

                                setRequestData({
                                    'allRequests': [],
                                    'linkedRequests': [],
                                    'filteredRequests': [],
                                    'addedRequests': [],
                                    'removedRequests': [],

                                    // Add Requests Search
                                    'addRequestsSearchInput': '',
                                    'addRequestSearchResults': [],

                                    // Table Search
                                    'tableSearchInput': ''
                                });

                                setDocument(undefined);
                                setPaneStatus('pending');

                            }}>
                                Cancel
                            </button>

                        </div>

                    </div>
                </dialog >
            </div>
        )
    }

}
